import UseContext from 'hooks/useContext';
import Button from 'components/button';
import Banner from 'components/banner/banner';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import PanelCard, {
  PANEL_TABLE_HEAD_CLASS,
  PANEL_TABLE_ROW_CLASS,
  SECTION_TITLE_CLASS,
} from 'components/card/PanelCard';
import { fillText } from 'components/perimeter/perimeterText';
import {
  NOTIFICATION_TYPES,
  allowsPerimeterLevel,
  toggleNotificationLevel,
} from 'components/perimeter/memberNotifications';

const COLUMNS = ['perimeter', 'all'];
// The columns of the table: the subject, then a narrow one per level.
const ROW = 'grid grid-cols-[1fr_6rem_6rem] items-center gap-2 px-4';

// The « Notifications » block of the contact screen, drawn as the company's
// « Coefficients de facturation » (PanelCard and its table classes). Like the
// perimeters block, it only shows the draft: the screen owns it and sends it
// on save. `title` replaces « Notifications »: a company's name, or null under
// a tab that already says it; `footerAction` sits at the right of the line
// under the table.
const CompanyContactNotifications = ({
  title,
  footerAction,
  status,
  levels,
  isPerimeterColumnDisabled,
  perimeterHint,
  error,
  onChange,
  onRetry,
}) => {
  const { language } = UseContext();
  const t = (key) =>
    language.getValue(`companyContacts.edit.notifications.${key}`, '');

  const isColumnDisabled = (type, column) =>
    column === 'perimeter' &&
    (isPerimeterColumnDisabled || !allowsPerimeterLevel(type));
  const isReady = status === 'ready' && Boolean(levels);
  const shownTitle = title === undefined ? t('title') : title;
  const hasFooter = status === 'ready' && (perimeterHint || footerAction);

  return (
    <div className={'flex flex-col gap-2'}>
      <section data-testid={'contact-notifications'} aria-label={shownTitle || t('title')}>
        <PanelCard
          title={shownTitle}
          titleClassName={SECTION_TITLE_CLASS}
          footer={
            hasFooter && (
              <div className={'flex items-center justify-between gap-3'}>
                {perimeterHint ? (
                  <p className={'px-1 text-xs text-gray-500'} data-testid={'contact-notifications-hint'}>
                    {perimeterHint}
                  </p>
                ) : (
                  <span />
                )}
                {footerAction}
              </div>
            )
          }
        >
          {status === 'loading' && (
            // The table to come, line for line: the page does not jump once read.
            <div aria-busy={'true'}>
              <span className={'sr-only'}>{t('loading')}</span>
              <div className={`${ROW} ${PANEL_TABLE_HEAD_CLASS}`}>
                <span>{t('name')}</span>
                {COLUMNS.map((column) => (
                  <span key={column} className={'text-center'}>
                    {t(column)}
                  </span>
                ))}
              </div>
              {NOTIFICATION_TYPES.map((type) => (
                <div key={type} className={`${ROW} ${PANEL_TABLE_ROW_CLASS}`}>
                  <span className={'flex flex-col gap-1.5'}>
                    <SkeletonLine className={'w-28'} />
                    <SkeletonLine className={'h-3 w-52'} />
                  </span>
                  {COLUMNS.map((column) => (
                    <span key={column} className={'flex justify-center'}>
                      <SkeletonLine className={'h-4 w-4'} />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
          {status === 'error' && (
            <div className={'flex items-center justify-between gap-3 px-4 py-3'}>
              <p className={'text-sm text-gray-500'}>{t('loadError')}</p>
              <Button size={'SM'} color={'SECONDARY'} onClick={onRetry}>
                {t('retry')}
              </Button>
            </div>
          )}
          {isReady && (
            <>
              <div className={`${ROW} ${PANEL_TABLE_HEAD_CLASS}`}>
                <span>{t('name')}</span>
                {COLUMNS.map((column) => (
                  <span key={column} className={'text-center'}>
                    {t(column)}
                  </span>
                ))}
              </div>
              {NOTIFICATION_TYPES.map((type) => (
                <div key={type} className={`${ROW} ${PANEL_TABLE_ROW_CLASS}`}>
                  <span className={'min-w-0'}>
                    <span className={'block text-sm text-gray-900'}>
                      {t(`types.${type}.label`)}
                    </span>
                    <span className={'block truncate text-[13px] text-gray-500'}>
                      {t(`types.${type}.description`)}
                    </span>
                  </span>
                  {COLUMNS.map((column) => {
                    const isDisabled = isColumnDisabled(type, column);
                    return (
                      <span key={column} className={'flex justify-center'}>
                        <input
                          type={'checkbox'}
                          aria-label={fillText(t('checkboxLabel'), {
                            type: t(`types.${type}.label`),
                            column: t(column),
                          })}
                          checked={levels[type] === column}
                          disabled={isDisabled}
                          onChange={() =>
                            onChange(
                              type,
                              toggleNotificationLevel(levels[type], column),
                            )
                          }
                          className={`checkbox-primary ${
                            isDisabled ? 'cursor-not-allowed opacity-50' : ''
                          }`}
                        />
                      </span>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </PanelCard>
      </section>
      {error ? <Banner type={'alert'}>{error}</Banner> : null}
    </div>
  );
};

export default CompanyContactNotifications;
